import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Badge, HoverCard, HoverCardContent, HoverCardTrigger } from "@tellescope/ui";
import { formatDateTime, sanitizeEmailHTMLContent } from "@tellescope/utils";
import { Forward } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface ForwardedEmailPreviewProps {
  email: TellescopeModelFromName<"emails">;
}

export const ForwardedEmailPreview = ({ email }: ForwardedEmailPreviewProps) => {
  const { t } = useTranslation("inbox");

  const preview = useMemo(() => {
    const fromEmail = email.fromEmailOverride ?? email.scheduledFromEmail ?? "";
    const toAddresses = [...(email.destination ?? []), ...(email.alternateToAddress ? [email.alternateToAddress] : [])];
    return { fromEmail, toAddresses, html: email.HTMLContent };
  }, [email]);

  return (
    <HoverCard openDelay={100} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Badge variant="secondary" className="gap-1 cursor-default mr-auto h-6 text-sm font-normal">
          <Forward />
          {t("Forwarding email")}
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent side="top" align="start" className="w-96 max-h-96 overflow-y-auto overflow-x-hidden">
        <div className="space-y-1 text-xs">
          {preview.fromEmail && (
            <div>
              <span className="font-semibold">{t("From:")}</span> {preview.fromEmail}
            </div>
          )}
          {email.timestamp && (
            <div>
              <span className="font-semibold">{t("Date:")}</span>{" "}
              {formatDateTime(email.timestamp, "ddd, MMM D, YYYY at hh:mm a")}
            </div>
          )}
          {email.subject && (
            <div>
              <span className="font-semibold">{t("Subject:")}</span> {email.subject}
            </div>
          )}
          {preview.toAddresses.length > 0 && (
            <div>
              <span className="font-semibold">{t("To:")}</span> {preview.toAddresses.join(", ")}
            </div>
          )}
          {email.cc && email.cc.length > 0 && (
            <div>
              <span className="font-semibold">{t("Cc:")}</span> {email.cc.join(", ")}
            </div>
          )}
          <div
            className="prose prose-sm w-full border-l-2 pl-2 mt-2 truncate text-wrap line-clamp-4 text-xs"
            dangerouslySetInnerHTML={{ __html: sanitizeEmailHTMLContent(preview.html ?? "") }}
          />
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
